/*
 * Copyright 2026 Jason Figge
 *
 * This file is part of Chip Hippo.
 *
 * Chip Hippo is free software: you can redistribute it and/or modify it under
 * the terms of the GNU General Public License as published by the Free
 * Software Foundation, either version 3 of the License, or (at your option)
 * any later version.
 *
 * Chip Hippo is distributed in the hope that it will be useful, but WITHOUT
 * ANY WARRANTY; without even the implied warranty of MERCHANTABILITY or
 * FITNESS FOR A PARTICULAR PURPOSE. See the GNU General Public License for
 * more details.
 *
 * You should have received a copy of the GNU General Public License along
 * with Chip Hippo. If not, see <https://www.gnu.org/licenses/>.
 */

// ohm-format.js — a resistance as it is PRINTED, in one place. Pure and
// DOM-free.
//
// IT IS ONE MODULE BECAUSE IT WAS TWO, AND THEY DISAGREED. `discrete-view.js`
// (the silkscreen on a resistor array) and `schematic-view.js` (the value text
// beside a symbol) each carried a private `formatOhms`, and the same resistor
// read differently depending on which view you were looking at:
//
//     value      desk        schematic
//     4753       4.75k       4.753k
//     999999     1000k       999.999k
//     1000000    1M          1000k
//     undefined  undefined   (blank)
//
// A 1 MΩ resistor labelled "1M" on the desk and "1000k" on the schematic is one
// part stating two values, and "undefined" printed on a part is a bug the user
// gets to read. Two copies of a formatting rule will always end up like this;
// one cannot.
//
// What a person TYPES as a resistance is read by the one parser every value
// field shares (component-value.js), not here.

import { formatWithPrefix } from "./si-value.js";

/** Prefix steps, largest first. */
const STEPS = Object.freeze([
  ["G", 1e9],
  ["M", 1e6],
  ["k", 1e3],
  ["", 1],
]);

/**
 * Format a resistance for printing — 4700 → "4.7k", 10000 → "10k", 220 → "220".
 *
 * Three significant figures, which is what a resistor's own colour code and a
 * parts list both speak, so a value carrying more is shown rounded rather than
 * spilling its full precision across a symbol ("4.753k" was the old schematic).
 * Rounding carries into the next prefix (999999 is "1M", never "1000k" — the
 * old desk formatter printed exactly that); see si-value.js.
 *
 * A value that is not a positive finite number formats as "" — nothing is
 * printed. `normalizeParams` guarantees a stored resistance is positive, so
 * this is unreachable through the catalog; it exists because the alternative
 * when it IS reached is drawing the word "undefined" onto a part.
 *
 * @param {number} ohms
 * @returns {string} the formatted resistance, with no unit appended
 */
export function formatOhms(ohms) {
  return formatWithPrefix(ohms, STEPS);
}
